// One notification: the post, its files, the comment thread, and — for the
// office — the acknowledgement trail.
//
// The chase list is the reason this screen exists. "Who has not acknowledged
// the new exclusion zone" is a question with a deadline attached, so it is
// shown as names, not a number.
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '../api';
import { nameOf, notifications, type PostDetail } from '../notificationsApi';
import { useSession } from '../session';
import { Banner } from '../ui';
import { Documents } from '../components/Documents';

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useSession();
  const [detail, setDetail] = useState<PostDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [reRequest, setReRequest] = useState(false);

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const result = await notifications.get(id);
      setDetail(result);
      setError(null);
      // Opening a post counts as seeing it (spec 07).
      if (!result.post.viewer.seen) await notifications.markSeen(id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load that announcement.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, failure: string) {
    setBusy(true);
    try {
      await action();
      await load();
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : failure);
    } finally {
      setBusy(false);
    }
  }

  if (error && detail === null) return <Banner kind="error">{error}</Banner>;
  if (!detail || !id) return <p className="muted">Loading…</p>;

  const { post, people } = detail;
  const owed = post.acknowledgement !== null && post.viewer.inAudience && !post.viewer.acknowledged;

  return (
    <section>
      <p className="footnote">
        <Link to="/app/announcements">← All announcements</Link>
      </p>

      {error && <Banner kind="error">{error}</Banner>}

      {post.archived && (
        <Banner kind="error">
          This announcement was deleted. It is kept here with its trail, and the crew can no
          longer see it.
        </Banner>
      )}

      <article className="panel post">
        <div className="post-head">
          <strong>{nameOf(people, post.authorId)}</strong>
          <span className="muted"> · {new Date(post.createdAt).toLocaleString('en-AU')}</span>
          {post.edited && <span className="muted"> · edited (version {post.version})</span>}
          {post.requiresAck && <span className="tag tag-ack">Needs acknowledgement</span>}
        </div>

        {editing === null ? (
          <p className="post-body">{post.body}</p>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (editing.trim() === '') return;
              void run(async () => {
                await notifications.update(id, {
                  body: editing.trim(),
                  ...(reRequest ? { reRequestAck: true } : {}),
                });
                setEditing(null);
                setReRequest(false);
              }, 'Could not save that change.');
            }}
          >
            <label htmlFor="edit-body">
              Edit the post
              <textarea
                id="edit-body"
                rows={4}
                value={editing}
                onChange={(event) => setEditing(event.target.value)}
              />
            </label>

            {/* The warning the spec asks for: an acknowledgement of the old
                wording is not an acknowledgement of the new wording, and the
                poster is the only one who can judge whether it matters. */}
            {post.acknowledgement && (
              <>
                <p className="footnote">
                  {post.acknowledgement.acknowledgedCount}{' '}
                  {post.acknowledgement.acknowledgedCount === 1 ? 'person has' : 'people have'}{' '}
                  already acknowledged this. Their acknowledgement stays on the record against
                  the version they read.
                </p>
                <label className="row">
                  <input
                    type="checkbox"
                    checked={reRequest}
                    onChange={(event) => setReRequest(event.target.checked)}
                  />
                  This has changed materially, ask everyone to acknowledge it again
                </label>
              </>
            )}

            <div className="post-actions">
              <button type="submit" disabled={busy || editing.trim() === ''}>
                Save changes
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setEditing(null);
                  setReRequest(false);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {post.viewer.canEdit && editing === null && !post.archived && (
          <p className="footnote">
            <button className="secondary" onClick={() => setEditing(post.body)}>
              Edit post
            </button>
          </p>
        )}

        {post.job && (
          <p className="footnote">
            About{' '}
            {isManager ? (
              <Link to={`/app/jobs/${post.job.id}`}>
                {post.job.number} · {post.job.title}
              </Link>
            ) : (
              <>
                {post.job.number} · {post.job.title}
              </>
            )}
          </p>
        )}

        {owed && (
          <button
            disabled={busy}
            onClick={() =>
              void run(() => notifications.acknowledge(id), 'Could not record that.')
            }
          >
            Acknowledge
          </button>
        )}
        {post.viewer.acknowledged && (
          <p className="tag tag-done">You acknowledged this</p>
        )}
      </article>

      {/* Managers and admins can attach files; the component enforces the same
          rule the server does. */}
      <Documents kind="post" ownerId={post.id} title="Files" />

      {post.acknowledgement && detail.acknowledgements.length + detail.outstandingUserIds.length > 0 && (
        <div className="panel">
          <h2>
            Acknowledged {post.acknowledgement.acknowledgedCount} of{' '}
            {post.acknowledgement.audienceSize}
          </h2>

          {detail.outstandingUserIds.length > 0 && (
            <>
              <h3>Still to acknowledge</h3>
              <ul className="checklist">
                {detail.outstandingUserIds.map((userId) => (
                  <li key={userId}>{nameOf(people, userId)}</li>
                ))}
              </ul>
            </>
          )}

          {detail.acknowledgements.length > 0 && (
            <>
              <h3>Acknowledged</h3>
              <ul className="checklist">
                {detail.acknowledgements.map((ack) => (
                  <li key={`${ack.userId}-${ack.version}`}>
                    {nameOf(people, ack.userId)} ·{' '}
                    {new Date(ack.acknowledgedAt).toLocaleString('en-AU')}
                    {!ack.current && (
                      <span className="muted">
                        {' '}
                        (against version {ack.version}, since changed)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {post.viewer.canEdit && (
            <p className="footnote">
              <button
                className="secondary"
                disabled={busy}
                onClick={() => {
                  if (
                    !window.confirm(
                      'Ask everyone to acknowledge this again? The previous acknowledgements ' +
                        'stay on the record.'
                    )
                  ) {
                    return;
                  }
                  void run(
                    () => notifications.update(id, { reRequestAck: true }),
                    'Could not re-request that.'
                  );
                }}
              >
                Re-request acknowledgement
              </button>
            </p>
          )}
        </div>
      )}

      <div className="panel">
        <h2>Comments</h2>

        {detail.comments.length === 0 && <p className="muted">No comments yet.</p>}

        <ul className="thread">
          {detail.comments.map((entry) => (
            <li key={entry.id}>
              <strong>{nameOf(people, entry.authorId)}</strong>{' '}
              <span className="muted">{new Date(entry.createdAt).toLocaleString('en-AU')}</span>
              {entry.deleted ? (
                <p className="muted">(removed)</p>
              ) : (
                <p>{entry.body}</p>
              )}
              {entry.canDelete && (
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => notifications.deleteComment(id, entry.id),
                      'Could not remove that comment.'
                    )
                  }
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>

        {post.commentsLocked ? (
          <p className="muted">This thread is locked; no new comments.</p>
        ) : (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (comment.trim() === '') return;
              void run(async () => {
                await notifications.comment(id, comment.trim());
                setComment('');
              }, 'Could not add that comment.');
            }}
          >
            <label htmlFor="new-comment">
              Add a comment
              <textarea
                id="new-comment"
                rows={2}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </label>
            <button type="submit" disabled={busy || comment.trim() === ''}>
              Comment
            </button>
          </form>
        )}

        {post.viewer.canLock && (
          <p className="footnote">
            <button
              className="secondary"
              disabled={busy}
              onClick={() =>
                void run(
                  () => notifications.lock(id, !post.commentsLocked),
                  'Could not change the thread.'
                )
              }
            >
              {post.commentsLocked ? 'Unlock thread' : 'Lock thread'}
            </button>
          </p>
        )}
      </div>

      {post.viewer.canEdit && !post.archived && (
        <p className="footnote">
          <button
            className="secondary"
            disabled={busy}
            onClick={() => {
              if (
                !window.confirm(
                  'Delete this announcement? It disappears from the crew’s feed, and its ' +
                    'acknowledgement trail is kept.'
                )
              ) {
                return;
              }
              void run(() => notifications.remove(id), 'Could not delete that.');
            }}
          >
            Delete announcement
          </button>
        </p>
      )}
    </section>
  );
}
